import React from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { OrgProvider } from './contexts/OrgContext';
import { LocationProvider } from './contexts/LocationContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';

// Auth pages
import LoginPage from './components/auth/LoginPage';
import SignupPage from './components/auth/SignupPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import AuthConfirmPage from './components/auth/AuthConfirmPage';
import LegalDocPage from './components/legal/LegalDocPage';
import MFAEnrollPage from './components/auth/MFAEnrollPage';
import MFAChallengePage from './components/auth/MFAChallengePage';
import InviteAcceptPage from './components/auth/InviteAcceptPage';
import ClientInvitePage from './components/auth/ClientInvitePage';
import OrgSignupPage from './components/auth/OrgSignupPage';
import RefRedirect from './components/auth/RefRedirect';

// Layouts & guards
import AppShell from './components/layout/AppShell';
import AdminShell from './components/layout/AdminShell';
import ProtectedRoute from './components/layout/ProtectedRoute';
import AdminRoute from './components/layout/AdminRoute';
import SubscriptionGate from './components/layout/SubscriptionGate';
import QuickBookPage from './components/booking/QuickBookPage';

// Billing
import SubscribePage from './components/billing/SubscribePage';
import NewOrgCheckoutPage from './components/billing/NewOrgCheckoutPage';
import SignupCompletePage from './components/billing/SignupCompletePage';
import Onboarding from './components/billing/Onboarding';

// App pages
import OrgDashboard from './components/dashboard/OrgDashboard';
import Scheduler from './components/scheduling/Scheduler';
import OrgSettingsPage from './components/settings/OrgSettingsPage';
import ContactsPage from './components/contacts/ContactsPage';
import ContactProfile from './components/contacts/ContactProfile';
import DirectoryPage from './components/directory/DirectoryPage';
import MemberList from './components/settings/MemberList';
import InvitationList from './components/settings/InvitationList';
import AppointmentTypesPage from './components/settings/AppointmentTypesPage';
import DocumentsPage from './components/documents/DocumentsPage';
import FormsPage from './components/forms/FormsPage';
import TbiAssessmentsPage from './components/assessments/TbiAssessmentsPage';
import AssessmentDetailPage from './components/assessments/AssessmentDetailPage';
import MocaConversational from './components/assessments/MocaConversational';
import VideoVisitPage from './components/telehealth/VideoVisitPage';
import TelehealthPage from './components/telehealth/TelehealthPage';
import ReferralConcierge from './components/referrals/ReferralConcierge';
import ReferralsPage from './components/referrals/ReferralsPage';
import CaseDetailPage from './components/cases/CaseDetailPage';
import NewCasePage from './components/cases/NewCasePage';
import CasesPage from './components/cases/CasesPage';

// LOP referrals are a demo-only surface for now (localStorage backend, same
// pattern as TBI assessments) — routes register only on demo builds so a
// prod deploy never exposes them.
const IS_DEMO = process.env.REACT_APP_DEMO === 'true';

// Client portal
import ClientShell from './components/client/ClientShell';
import ClientDashboard from './components/client/ClientDashboard';
import ClientProfile from './components/client/ClientProfile';
import FormSigningPage from './components/client/FormSigningPage';
import ClientFormsPage from './components/client/ClientFormsPage';
import ClientQuestionnairePage from './components/client/ClientQuestionnairePage';
import ClientDocumentsPage from './components/client/ClientDocumentsPage';
import FindCarePage from './components/client/FindCarePage';

// Admin pages
import AdminDashboard from './components/admin/AdminDashboard';
import OrganizationList from './components/admin/OrganizationList';
import OrganizationCreate from './components/admin/OrganizationCreate';
import OrganizationDetail from './components/admin/OrganizationProfile';
import UserList from './components/admin/UserList';
import UserDetail from './components/admin/UserDetail';
import ReferralLinks from './components/admin/ReferralLinks';
import CaseReferrals from './components/admin/CaseReferrals';
import AdminMapPage from './components/admin/AdminMapPage';

// Directory org profile
import OrgProfile from './components/directory/OrgProfile';

// Placeholder page
function Placeholder({ title }) {
  return (
    <div className="flex-1 flex items-center justify-center bg-primary">
      <div className="text-center">
        <h2 className="text-[20px] font-bold text-primary-c mb-2">{title}</h2>
        <p className="text-[13px] text-muted-c">Coming soon</p>
      </div>
    </div>
  );
}

// Root layout that provides Auth + Org + Subscription context to all routes
function RootLayout() {
  return (
    <AuthProvider>
      <OrgProvider>
        <LocationProvider>
          <SubscriptionProvider>
            <Outlet />
          </SubscriptionProvider>
        </LocationProvider>
      </OrgProvider>
    </AuthProvider>
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public auth routes
      { path: '/login', element: <LoginPage /> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/auth/confirm', element: <AuthConfirmPage /> },
      { path: '/legal/baa', element: <LegalDocPage doc="baa" /> },
      { path: '/invite/:token', element: <InviteAcceptPage /> },
      { path: '/client-invite/:token', element: <ClientInvitePage /> },
      { path: '/register', element: <OrgSignupPage /> },
      // Vanity referral links (QR/print): /r/mike -> capture + /register
      { path: '/r/:code', element: <RefRedirect /> },

      // Guest video visit — PUBLIC: the unguessable token in the invite
      // link is the credential (traded server-side for Chime credentials).
      { path: '/visit/:guestToken', element: <VideoVisitPage guest /> },
      // Quick booking from the referral text — PUBLIC, token-credentialed.
      { path: '/book/:token', element: <QuickBookPage /> },

      // Auth required (no org required)
      { path: '/mfa/enroll', element: <MFAEnrollPage /> },
      { path: '/mfa/verify', element: <MFAChallengePage /> },

      // New-org signup pages — auth required, but NO org yet (the whole
      // point: the org is created only after Stripe checkout). Both pages
      // check `session` themselves and redirect to /login if missing.
      { path: '/signup/checkout', element: <NewOrgCheckoutPage /> },
      { path: '/signup/complete', element: <SignupCompletePage /> },

      // Full-bleed protected surfaces — auth + MFA + org but no app shell.
      // The post-checkout onboarding and subscribe page have their own layouts.
      {
        element: <ProtectedRoute />,
        children: [
          { path: '/app/welcome',   element: <Onboarding /> },
          { path: '/app/subscribe', element: <SubscribePage /> },
          // Conversational MoCA — full-bleed surface (no app shell) so the
          // administrator stays focused on one step at a time.
          { path: '/app/tbi-assessments/moca/new', element: <MocaConversational /> },
          // Telehealth room — one route for both sides of the call: the bare
          // ProtectedRoute admits any org member (clients included), and the
          // page itself asks the API which role it is talking to.
          { path: '/telehealth/:sessionId', element: <VideoVisitPage /> },
        ],
      },

      // Protected app routes (auth + MFA + org + subscription gate for medical_practice)
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <AppShell />,
            children: [
              {
                element: <SubscriptionGate />,
                children: [
                  { path: '/app', element: <Navigate to="/app/dashboard" replace /> },
                  { path: '/app/dashboard', element: <OrgDashboard /> },
                  { path: '/app/contacts', element: <ContactsPage /> },
                  { path: '/app/contacts/:id', element: <ContactProfile /> },
                  { path: '/app/cases', element: <CasesPage /> },
                  { path: '/app/cases/new', element: <NewCasePage /> },
                  { path: '/app/cases/:id', element: <CaseDetailPage /> },
                  { path: '/app/scheduling', element: <Scheduler /> },
                  { path: '/app/telehealth', element: <TelehealthPage /> },
                  { path: '/app/referrals', element: <ReferralsPage /> },
                  { path: '/app/directory', element: <DirectoryPage /> },
                  { path: '/app/directory/:id', element: <OrgProfile /> },
                  { path: '/app/documents', element: <DocumentsPage /> },
                  { path: '/app/forms', element: <FormsPage /> },
                  { path: '/app/tbi-assessments', element: <TbiAssessmentsPage /> },
                  { path: '/app/tbi-assessments/:id', element: <AssessmentDetailPage /> },
                  { path: '/app/referrals/concierge', element: <ReferralConcierge /> },
                  { path: '/app/settings', element: <OrgSettingsPage /> },
                ],
              },
            ],
          },
        ],
      },

      // Client portal routes (auth + client role)
      {
        element: <ProtectedRoute allowedRoles={['client']} />,
        children: [
          {
            element: <ClientShell />,
            children: [
              { path: '/client', element: <ClientDashboard /> },
              { path: '/client/directory', element: <DirectoryPage /> },
              { path: '/client/find-care', element: <FindCarePage /> },
              { path: '/client/directory/:id', element: <OrgProfile /> },
              { path: '/client/profile', element: <ClientProfile /> },
              { path: '/client/forms', element: <ClientFormsPage /> },
              { path: '/client/forms/:id', element: <FormSigningPage /> },
              { path: '/client/questionnaires/:id', element: <ClientQuestionnairePage /> },
              { path: '/client/documents', element: <ClientDocumentsPage /> },
            ],
          },
        ],
      },

      // Admin routes (platform_admin only)
      {
        element: <AdminRoute />,
        children: [
          {
            element: <AdminShell />,
            children: [
              { path: '/admin', element: <Navigate to="/admin/dashboard" replace /> },
              { path: '/admin/dashboard', element: <AdminDashboard /> },
              { path: '/admin/organizations', element: <OrganizationList /> },
              { path: '/admin/marketers', element: <OrganizationList mode="marketers" /> },
              { path: '/admin/organizations/new', element: <OrganizationCreate /> },
              { path: '/admin/organizations/:id', element: <OrganizationDetail /> },
              { path: '/admin/users', element: <UserList /> },
              { path: '/admin/users/:id', element: <UserDetail /> },
              { path: '/admin/referrals', element: <ReferralLinks /> },
              { path: '/admin/case-referrals', element: <CaseReferrals /> },
              { path: '/admin/map', element: <AdminMapPage /> },
              { path: '/admin/directory', element: <DirectoryPage /> },
              { path: '/admin/directory/:id', element: <OrgProfile /> },
            ],
          },
        ],
      },

      // Catch-all
      { path: '*', element: <Navigate to="/login" replace /> },
    ],
  },
]);
